import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/features/auth/contexts/AuthContext';

export interface StudioMembership {
  studioId: string;
  studioName: string;
  role: 'resident' | 'guest';
  status: 'active' | 'archived';
  isOwner: boolean;
}

interface StudioContextValue {
  membership: StudioMembership | null;
  loading: boolean;
  refetch: () => void;
}

const StudioContext = createContext<StudioContextValue | null>(null);

export function StudioProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [membership, setMembership] = useState<StudioMembership | null>(null);
  const [loading, setLoading] = useState(true);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    if (!user) { setMembership(null); setLoading(false); return; }
    let cancelled = false;
    setLoading(true);

    async function load() {
      // 1. Check if this user owns a studio
      const { data: owned } = await supabase
        .from('studios')
        .select('id, name')
        .eq('owner_id', user!.id)
        .maybeSingle();

      if (owned && !cancelled) {
        setMembership({ studioId: owned.id, studioName: owned.name, role: 'resident', status: 'active', isOwner: true });
        setLoading(false);
        return;
      }

      // 2. Check if this user is a member
      const { data: row } = await supabase
        .from('studio_members')
        .select('studio_id, role, status, studios(name)')
        .eq('user_id', user!.id)
        .maybeSingle();

      if (!cancelled) {
        if (row) {
          setMembership({
            studioId: row.studio_id,
            studioName: (row.studios as any)?.name ?? '',
            role: row.role as 'resident' | 'guest',
            status: row.status as 'active' | 'archived',
            isOwner: false,
          });
        } else {
          setMembership(null);
        }
        setLoading(false);
      }
    }

    load();
    return () => { cancelled = true; };
  }, [user?.id, tick]);

  const refetch = () => setTick(t => t + 1);

  return (
    <StudioContext.Provider value={{ membership, loading, refetch }}>
      {children}
    </StudioContext.Provider>
  );
}

export function useStudio() {
  const ctx = useContext(StudioContext);
  if (!ctx) throw new Error('useStudio must be used within StudioProvider');
  return ctx;
}
